import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";

const FeedbackContext = createContext(null);

const DEFAULT_DRAFT = Object.freeze({
    featureArea: "global_review",
    targetType: "review_page",
    targetId: null,
    aiCommentId: null,
    contextJson: null,
    presetReason: null,
    badgeLabel: null,
});

/**
 * Provider posé au plus haut de la review page.
 * Il porte les ids stables (analyseId, documentVersionId, workspaceId)
 * et expose `openFeedback({...})` pour ouvrir le drawer avec un contexte préréglé
 * depuis n'importe quel composant enfant (cartes, mémo, dock global...).
 */
export const FeedbackProvider = ({
    analyseId = null,
    documentVersionId = null,
    workspaceId = null,
    children,
}) => {
    const [open, setOpen] = useState(false);
    const [draft, setDraft] = useState(DEFAULT_DRAFT);
    // ref pour permettre aux auto-captures de lire le contexte courant sans rerender
    const stableRef = useRef({ analyseId, documentVersionId, workspaceId });
    stableRef.current = { analyseId, documentVersionId, workspaceId };

    const openFeedback = useCallback((next = {}) => {
        setDraft({
            ...DEFAULT_DRAFT,
            ...next,
            contextJson: next.contextJson || null,
        });
        setOpen(true);
    }, []);

    const closeFeedback = useCallback(() => setOpen(false), []);

    const value = useMemo(() => ({
        open,
        draft,
        analyseId,
        documentVersionId,
        workspaceId,
        openFeedback,
        closeFeedback,
        getStableContext: () => stableRef.current,
    }), [open, draft, analyseId, documentVersionId, workspaceId, openFeedback, closeFeedback]);

    return (
        <FeedbackContext.Provider value={value}>
            {children}
        </FeedbackContext.Provider>
    );
};

export const useFeedback = () => {
    const ctx = useContext(FeedbackContext);
    // Hook safe : si un consommateur est rendu hors provider, on ne plante pas.
    if (!ctx) {
        return {
            open: false,
            draft: DEFAULT_DRAFT,
            analyseId: null,
            documentVersionId: null,
            workspaceId: null,
            openFeedback: () => {},
            closeFeedback: () => {},
            getStableContext: () => ({ analyseId: null, documentVersionId: null, workspaceId: null }),
        };
    }
    return ctx;
};

export default FeedbackContext;

